import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import i18n from '../i18n/config';
import { getWsUrl } from '../utils/api-url';

interface UseWebSocketOptions {
  gameId?: string;
  playerId?: string;
  onTurnCompleted?: (data: any) => void;
  onTurnProcessing?: (data: any) => void;
  onTurnMedia?: (data: any) => void;
  onPlayerOnline?: (data: any) => void;
  onPlayerOffline?: (data: any) => void;
  onChatMessage?: (data: any) => void;
  onPlayerTyping?: (data: any) => void;
}

export function useWebSocket(options: UseWebSocketOptions) {
  const {
    gameId,
    playerId,
    onTurnCompleted,
    onTurnProcessing,
    onTurnMedia,
    onPlayerOnline,
    onPlayerOffline,
    onChatMessage,
    onPlayerTyping,
  } = options;

  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlinePlayers, setOnlinePlayers] = useState<any[]>([]);

  useEffect(() => {
    // Connect to WebSocket server
    const wsUrl = getWsUrl();
    const socket = io(wsUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('✅ WebSocket connected');
      setIsConnected(true);

      // Join game room if gameId provided
      if (gameId) {
        socket.emit('game:join', { gameId, playerId });
      }
    });

    socket.on('disconnect', () => {
      console.log('❌ WebSocket disconnected');
      setIsConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      setIsConnected(false);
    });

    // Turn events
    if (onTurnProcessing) {
      socket.on('turn:processing', onTurnProcessing);
    }

    if (onTurnCompleted) {
      socket.on('turn:completed', onTurnCompleted);
    }

    if (onTurnMedia) {
      socket.on('turn:media', onTurnMedia);
    }

    socket.on('turn:error', (data) => {
      console.error('Turn processing error:', data.error);
      alert(i18n.t('errors.turnProcessing', { error: data.error }));
    });

    // Player online/offline
    socket.on('players:online', (data) => {
      setOnlinePlayers(data.players);
    });

    if (onPlayerOnline) {
      socket.on('player:online', (data) => {
        onPlayerOnline(data);
        setOnlinePlayers((prev) => {
          const exists = prev.find(p => p.id === data.playerId);
          if (!exists) {
            return [...prev, { id: data.playerId }];
          }
          return prev;
        });
      });
    }

    if (onPlayerOffline) {
      socket.on('player:offline', (data) => {
        onPlayerOffline(data);
        setOnlinePlayers((prev) => prev.filter(p => p.id !== data.playerId));
      });
    }

    // Chat events
    if (onChatMessage) {
      socket.on('chat:message', onChatMessage);
    }

    if (onPlayerTyping) {
      socket.on('player:typing', onPlayerTyping);
    }

    return () => {
      if (gameId && playerId) {
        socket.emit('game:leave', { gameId, playerId });
      }
      socket.disconnect();
    };
  }, [gameId, playerId]);

  const sendChatMessage = (message: string, playerName: string) => {
    if (socketRef.current && gameId && playerId) {
      socketRef.current.emit('chat:message', {
        gameId,
        playerId,
        playerName,
        message,
      });
    }
  };

  const setTyping = (isTyping: boolean, playerName: string) => {
    if (socketRef.current && gameId && playerId) {
      socketRef.current.emit('player:typing', {
        gameId,
        playerId,
        playerName,
        isTyping,
      });
    }
  };

  return {
    socket: socketRef.current,
    isConnected,
    onlinePlayers,
    sendChatMessage,
    setTyping,
  };
}
